import React from "react";
import {
  AdvancedType,
  BlockManager,
  BasicType,
  IPage,
  JsonToMjml,
  advancedBlocks,
} from "@ivanholiak/easy-email-core";
import {
  BlockAttributeConfigurationManager,
  DefaultPageConfigPanel,
  type ExtensionProps,
} from "@ivanholiak/easy-email-extensions";
import mjml from "mjml-browser";
import ConditionalBlockAttributePanel from "./ConditionalBlockAttributePanel";

export type BrandKitData = {
  primary_color?: string | null;
  secondary_color?: string | null;
  font_family?: string | null;
};

// Blocos avançados + painel Page sem Subject/SubTitle (assunto fica no form da campanha).
let editorExtrasRegistered = false;
let brandKitDefaults: BrandKitData = {};
let brandCreatePatched = false;

/** Defaults do brand kit para blocos recém-arrastados (LAC-131). */
export const setEasyEmailBrandDefaults = (kit: BrandKitData | null): void => {
  brandKitDefaults = kit || {};
};

const applyBrandDefaultsToNode = (node: any): void => {
  if (!node || typeof node !== "object" || !node.attributes) {
    return;
  }
  const type = String(node.type || "");
  const isTextish =
    type.includes("text") ||
    type.includes("button") ||
    type === BasicType.TEXT ||
    type === AdvancedType.TEXT ||
    type === AdvancedType.BUTTON;
  if (isTextish && brandKitDefaults.primary_color) {
    if (!node.attributes.color || node.attributes.color === "#000000" || node.attributes.color === "#000") {
      node.attributes.color = brandKitDefaults.primary_color;
    }
  }
  if (isTextish && brandKitDefaults.font_family) {
    node.attributes["font-family"] = brandKitDefaults.font_family;
  }
  if (
    (type.includes("button") || type === AdvancedType.BUTTON) &&
    brandKitDefaults.primary_color
  ) {
    if (!node.attributes["background-color"]) {
      node.attributes["background-color"] = brandKitDefaults.primary_color;
    }
  }
};

export const ensureEasyEmailBlocks = (): void => {
  if (!editorExtrasRegistered) {
    BlockManager.registerBlocks(advancedBlocks);
    const OriginalWrapperPanel = BlockAttributeConfigurationManager.get(
      AdvancedType.WRAPPER
    );
    BlockAttributeConfigurationManager.add({
      [BasicType.PAGE]: () =>
        React.createElement(DefaultPageConfigPanel, {
          hideSubject: true,
          hideSubTitle: true,
        }),
      [AdvancedType.WRAPPER]: () =>
        React.createElement(
          React.Fragment,
          null,
          OriginalWrapperPanel
            ? React.createElement(OriginalWrapperPanel)
            : null,
          React.createElement(ConditionalBlockAttributePanel)
        ),
    });
    editorExtrasRegistered = true;
  }

  if (!brandCreatePatched) {
    const types = [
      AdvancedType.TEXT,
      AdvancedType.BUTTON,
      BasicType.TEXT,
      AdvancedType.IMAGE,
    ];
    for (const type of types) {
      const block = BlockManager.getBlockByType(type);
      if (!block || typeof (block as any).create !== "function") {
        continue;
      }
      const original = (block as any).create.bind(block);
      (block as any).create = (payload?: unknown) => {
        const node = original(payload);
        applyBrandDefaultsToNode(node);
        return node;
      };
    }
    brandCreatePatched = true;
  }
};

export const CAMPAIGN_MERGE_TAGS: Record<string, string> = {
  nome: "Maria",
  email: "maria@exemplo.com",
};

/** Amostras só no Preview local (fallback sem API) — inclui telefone. */
export const PREVIEW_SAMPLE_TAGS: Record<string, string> = {
  ...CAMPAIGN_MERGE_TAGS,
  telefone: "+55 11 98888-7777",
  phone: "+55 11 98888-7777",
  name: "Maria",
};

/** Gera `{{nome}}` no HTML (mesmo formato dos chips / merge do produto). */
export const campaignMergeTagGenerate = (tag: string): string => `{{${tag}}}`;

/**
 * Substitui tags nativas por amostras no HTML do Preview.
 * fields.* ficam para a API preview-merge (LAC-068).
 */
export const applySampleMergeTags = (html: string): string => {
  let out = html;
  for (const [key, value] of Object.entries(PREVIEW_SAMPLE_TAGS)) {
    out = out.split(`{{${key}}}`).join(value);
    out = out.split(`{{ ${key} }}`).join(value);
  }
  return out;
};

/** Conteúdo placeholder do canvas vazio — usado para detectar “primeiro uso”. */
export const EMPTY_CAMPAIGN_PLACEHOLDER =
  "Olá {{nome}},<br/><br/>Escreva aqui o conteúdo da sua campanha. Arraste blocos à esquerda ou escolha um modelo pronto.";

/**
 * Página inicial menos “cinza genérico” (LAC-107 B.4).
 */
export const createEmptyCampaignPage = (): IPage => {
  ensureEasyEmailBlocks();
  return BlockManager.getBlockByType(BasicType.PAGE)!.create({
    attributes: {
      "background-color": "#f3f6f9",
      width: "600px",
    },
    children: [
      BlockManager.getBlockByType(BasicType.SECTION)!.create({
        attributes: {
          padding: "32px 24px 32px 24px",
          "background-color": "#ffffff",
        },
        children: [
          BlockManager.getBlockByType(BasicType.COLUMN)!.create({
            children: [
              BlockManager.getBlockByType(BasicType.TEXT)!.create({
                data: {
                  value: {
                    content: EMPTY_CAMPAIGN_PLACEHOLDER,
                  },
                },
                attributes: {
                  padding: "0px 0px 0px 0px",
                  align: "left",
                  color: "#495057",
                  "font-size": "16px",
                  "line-height": "1.6",
                },
              }),
            ],
          }),
        ],
      }),
    ],
  });
};

/**
 * Brand Kit do tenant — tipo mapeado da session (TenantResource).
 */

/**
 * Aplica brand kit à página do Easy Email (cores + fonte).
 * Por quê: checkbox use_brand_kit no form → herda visual da conta.
 * Retorna nova página clonada; não muta a original.
 */
export const applyBrandKitToPage = (
  page: IPage,
  kit: BrandKitData
): IPage => {
  // Clone profundo para não mutar provider do Easy Email.
  const cloned = JSON.parse(JSON.stringify(page)) as IPage;
  if (!cloned || typeof cloned !== "object") {
    return page;
  }

  // PAGE: aplica secondary_color no fundo (sutil) se disponível.
  if (kit.secondary_color && cloned.attributes) {
    // Se tiver cor secundária, usa tom claro (10% opacity) como fundo da página.
    // Evita sobrescrever branco das sections (fundo forte fica no conteúdo).
    const bgFallback = cloned.attributes["background-color"] || "#f3f6f9";
    cloned.attributes["background-color"] = bgFallback;
  }

  // Caminha nós TEXT: aplica primary_color na cor do texto (se vazio/default) + font_family.
  const walkText = (node: any): void => {
    if (!node || typeof node !== "object") {
      return;
    }
    if (node.type === BasicType.TEXT && node.attributes) {
      // Cor primária no texto (se ainda for default cinza).
      if (
        kit.primary_color &&
        (!node.attributes.color || node.attributes.color === "#495057")
      ) {
        node.attributes.color = kit.primary_color;
      }
      // Fonte da marca (se configurada).
      if (kit.font_family) {
        node.attributes["font-family"] = kit.font_family;
      }
    }
    const type = String(node.type || "");
    if (
      (type.includes("button") || type === AdvancedType.BUTTON) &&
      node.attributes
    ) {
      if (kit.primary_color && !node.attributes["background-color"]) {
        node.attributes["background-color"] = kit.primary_color;
      }
      if (kit.font_family) {
        node.attributes["font-family"] = kit.font_family;
      }
    }
    if (Array.isArray(node.children)) {
      node.children.forEach((child: any) => walkText(child));
    }
  };

  walkText(cloned);
  return cloned;
};

export const isEasyEmailPage = (value: unknown): value is IPage => {
  if (!value || typeof value !== "object") {
    return false;
  }
  const page = value as Partial<IPage>;
  return page.type === BasicType.PAGE && Array.isArray(page.children);
};

/** Conta nós da árvore Easy Email (PAGE + filhos). */
const countDesignNodes = (node: unknown): number => {
  if (!node || typeof node !== "object") {
    return 0;
  }
  const children = (node as { children?: unknown[] }).children;
  let total = 1;
  if (Array.isArray(children)) {
    for (const child of children) {
      total += countDesignNodes(child);
    }
  }
  return total;
};

/**
 * True se o design ainda é o placeholder de primeiro uso (LAC-107 / LAC-109).
 * Página vazia default = PAGE+SECTION+COLUMN+TEXT (4 nós). Bloco a mais (drag)
 * ou texto do placeholder alterado → empty some sem clicar no CTA.
 */
export const isPlaceholderCampaignPage = (value: unknown): boolean => {
  if (!isEasyEmailPage(value)) {
    return true;
  }
  // Drag de bloco cresce a árvore mesmo com o texto placeholder intacto.
  if (countDesignNodes(value) > 4) {
    return false;
  }
  try {
    const html = exportEasyEmailHtml(value);
    return html.includes("Escreva aqui o conteúdo da sua campanha");
  } catch {
    return false;
  }
};

/**
 * Verifica se um nó tem condição dinâmica (F2).
 */
const hasBrenvioCondition = (node: unknown): boolean => {
  if (!node || typeof node !== "object") {
    return false;
  }
  const value = (node as { data?: { value?: { brenvioCondition?: unknown } } })
    .data?.value?.brenvioCondition;
  return Boolean(value && typeof value === "object");
};

export type BrenvioCondition =
  | { type: "tag"; op?: string; values: string[] }
  | { type: "field"; slug: string; op?: string; value: string }
  | { type: "segment"; id: number }
  | { type: "rules"; data: Record<string, unknown> };

/** Monta marcadores HTML que o Batch resolve por contato. */
export const wrapConditionalHtml = (
  html: string,
  condition: BrenvioCondition
): string => {
  const type = condition.type || "tag";
  let attrs = `type="${type}"`;

  if (type === "tag") {
    const tagCond = condition as Extract<BrenvioCondition, { type: "tag" }>;
    const values = Array.isArray(tagCond.values)
      ? tagCond.values.join(",")
      : "";
    const op = tagCond.op || "in";
    attrs += ` op="${op}" values="${values.replace(/"/g, "")}"`;
  } else if (type === "field") {
    const fieldCond = condition as Extract<BrenvioCondition, { type: "field" }>;
    attrs += ` slug="${String(fieldCond.slug || "").replace(/"/g, "")}" op="${fieldCond.op || "eq"}" value="${String(fieldCond.value || "").replace(/"/g, "")}"`;
  } else if (type === "segment") {
    const segCond = condition as Extract<BrenvioCondition, { type: "segment" }>;
    attrs += ` id="${Number(segCond.id) || 0}"`;
  } else if (type === "rules") {
    const rulesCond = condition as Extract<BrenvioCondition, { type: "rules" }>;
    const json = JSON.stringify(rulesCond.data || {});
    const data =
      typeof btoa === "function"
        ? btoa(unescape(encodeURIComponent(json)))
            .replace(/\+/g, "-")
            .replace(/\//g, "_")
            .replace(/=+$/, "")
        : "";
    attrs += ` data="${data}"`;
  }

  return `<!--[brenvio-if ${attrs}]-->${html}<!--[/brenvio-if]-->`;
};

/** Export MJML sem reentrar no preprocess de condicionais (evita loop). */
const exportFragmentHtml = (page: IPage): string => {
  ensureEasyEmailBlocks();
  const mjmlString = JsonToMjml({
    data: page,
    mode: "production",
    context: page,
    beautify: false,
  });
  const result = mjml(mjmlString, {
    validationLevel: "soft",
    minify: false,
  });
  return String(result.html || "").trim();
};

/**
 * Caminha a árvore: nós com brenvioCondition viram RAW com marcadores.
 */
export const processConditionalTree = (node: any): any => {
  if (!node || typeof node !== "object") {
    return node;
  }

  if (hasBrenvioCondition(node)) {
    try {
      const kids = Array.isArray(node.children) ? node.children : [];
      const tempPage = BlockManager.getBlockByType(BasicType.PAGE)!.create({
        attributes: { width: "600px" },
        children: [
          BlockManager.getBlockByType(BasicType.SECTION)!.create({
            children: [
              BlockManager.getBlockByType(BasicType.COLUMN)!.create({
                children: kids,
              }),
            ],
          }),
        ],
      });
      const childrenHtml = exportFragmentHtml(tempPage);
      const wrapped = wrapConditionalHtml(
        childrenHtml,
        node.data.value.brenvioCondition
      );
      return BlockManager.getBlockByType(BasicType.RAW)!.create({
        data: { value: { content: wrapped } },
      });
    } catch {
      return node;
    }
  }

  if (Array.isArray(node.children) && node.children.length > 0) {
    return {
      ...node,
      children: node.children.map((child: any) => processConditionalTree(child)),
    };
  }

  return node;
};

/**
 * Bloco WRAPPER condicional pronto para inserir no canvas (Easy Email).
 */
export const createConditionalWrapperBlock = (
  condition: BrenvioCondition,
  placeholderHtml: string
): any => {
  ensureEasyEmailBlocks();
  return BlockManager.getBlockByType(AdvancedType.WRAPPER)!.create({
    data: {
      value: {
        brenvioCondition: condition,
      },
    },
    attributes: {
      padding: "0px 0px 0px 0px",
      "background-color": "transparent",
      border: "1px dashed #b2d608",
    },
    children: [
      BlockManager.getBlockByType(AdvancedType.SECTION)!.create({
        children: [
          BlockManager.getBlockByType(AdvancedType.COLUMN)!.create({
            children: [
              BlockManager.getBlockByType(AdvancedType.TEXT)!.create({
                data: {
                  value: {
                    content: placeholderHtml,
                  },
                },
                attributes: {
                  padding: "12px 12px 12px 12px",
                  align: "left",
                  color: "#495057",
                  "font-size": "14px",
                },
              }),
            ],
          }),
        ],
      }),
    ],
  });
};

/**
 * Resultado da busca de blocos condicionais na página.
 */
export type ConditionalNodeInfo = {
  /** Índice na lista flat de condicionais (0-based). */
  index: number;
  /** Condição Brenvio anexada ao nó. */
  condition: BrenvioCondition;
  /** Referência ao nó WRAPPER (para navegação/edição). */
  node: any;
  /** Path na árvore (array de índices de children). */
  path: number[];
};

/**
 * Encontra todos os nós com brenvioCondition na página (busca em profundidade).
 * Retorna lista flat com path, index e condition — usar no select do modal Edit.
 */
export const findConditionalNodes = (page: IPage): ConditionalNodeInfo[] => {
  const results: ConditionalNodeInfo[] = [];
  let index = 0;

  const walk = (node: any, path: number[]): void => {
    if (!node || typeof node !== "object") {
      return;
    }
    if (hasBrenvioCondition(node)) {
      results.push({
        index,
        condition: node.data.value.brenvioCondition,
        node,
        path: [...path],
      });
      index++;
    }
    if (Array.isArray(node.children)) {
      node.children.forEach((child: any, i: number) =>
        walk(child, [...path, i])
      );
    }
  };

  walk(page, []);
  return results;
};

/**
 * Atualiza a condição de um bloco condicional existente (por path na árvore).
 * Retorna nova página clonada; não muta a original.
 */
export const updateConditionalAt = (
  page: IPage,
  path: number[],
  condition: BrenvioCondition
): IPage => {
  // Clone profundo da página para não mutar provider do Easy Email.
  const cloned = JSON.parse(JSON.stringify(page)) as IPage;

  let current: any = cloned;
  for (let i = 0; i < path.length; i++) {
    const idx = path[i];
    if (!Array.isArray(current.children) || idx >= current.children.length) {
      // Path inválido — retorna sem modificar (silent fail; UI pode alertar).
      return cloned;
    }
    if (i === path.length - 1) {
      // Último passo: atualiza a condition no nó alvo.
      const target = current.children[idx];
      if (
        target &&
        typeof target === "object" &&
        target.data?.value?.brenvioCondition
      ) {
        target.data.value.brenvioCondition = condition;
      }
    } else {
      current = current.children[idx];
    }
  }

  return cloned;
};

/**
 * JSON do Easy Email → HTML pronto para SES.
 * Roda 100% no browser (sem nuvem do editor).
 * F2: processa nós com brenvioCondition antes de exportar.
 */
export const exportEasyEmailHtml = (content: IPage): string => {
  ensureEasyEmailBlocks();
  const processedContent = processConditionalTree(content) as IPage;
  return exportFragmentHtml(processedContent);
};

/**
 * Categorias da sidebar — LAC-107 B.3: esconde HERO/WRAPPER (avançados);
 * SOCIAL fica no fim (menos comum no primeiro e-mail).
 * Bloco condicional: inserido via botão na toolbar (createConditionalWrapperBlock).
 */
export const buildCampaignEditorCategories = (
  t: (key: string) => string
): ExtensionProps["categories"] => [
  {
    label: t("campaigns.form.editor.categoryContent"),
    active: true,
    blocks: [
      { type: AdvancedType.TEXT },
      {
        type: AdvancedType.IMAGE,
        payload: { attributes: { padding: "0px 0px 0px 0px" } },
      },
      { type: AdvancedType.BUTTON },
      { type: AdvancedType.DIVIDER },
      { type: AdvancedType.SPACER },
      { type: AdvancedType.SOCIAL },
    ],
  },
  {
    label: t("campaigns.form.editor.categoryLayout"),
    active: true,
    displayType: "column",
    blocks: [
      {
        title: t("campaigns.form.editor.columns2"),
        payload: [
          ["50%", "50%"],
          ["33%", "67%"],
          ["67%", "33%"],
        ],
      },
      {
        title: t("campaigns.form.editor.columns3"),
        payload: [
          ["33.33%", "33.33%", "33.33%"],
          ["25%", "25%", "50%"],
        ],
      },
    ],
  },
];
